import { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Grid, Stack, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import MemberCard from './components/MemberCard';
import { useCollectiveMembers } from './hooks/useCollectiveMembers';
import CollectiveFilterModal from 'components/CollectiveFilterModal';
import FilterButton from 'components/FilterButton/FilterButton';
import Pagination from 'components/Pagination/Pagination';
import Spin from 'components/Spin';
import { useCollectiveFilter } from 'contexts/CollectiveFilterContext';
import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useIsMobile } from 'utils/mediaQueries';

const CollectiveProfileMembersPage = () => {
  const isMobile = useIsMobile();
  const { user } = useUser();
  const { filter, clearFilter } = useCollectiveFilter();
  const [pageLevel1, setPageLevel1] = useState(1);
  const [pageLevel2, setPageLevel2] = useState(1);
  const [pageOthers, setPageOthers] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const size = isMobile ? 3 : 9;
  const { selectMenuByDocumentId } = useMenuSelection();
  const { menu } = useMenu();

  // Add refresh triggers for each data section
  const [refreshTriggerLevel1, setRefreshTriggerLevel1] = useState(0);
  const [refreshTriggerLevel2, setRefreshTriggerLevel2] = useState(0);
  const [refreshTriggerOthers, setRefreshTriggerOthers] = useState(0);

  useEffect(() => {
    return () => {
      if (!filter.applyGlobally) {
        clearFilter();
      }
    };
  }, [filter.applyGlobally, clearFilter]);

  const baseParams = {
    user_reference: user?.id,
    teams: filter.teams.length ? filter.teams.join(',') : undefined,
    edge_types: filter.connectionTypes.length ? filter.connectionTypes.join(',') : undefined,
  };

  const {
    data: dataLevel1,
    isLoading: loadingLevel1,
    error: errorLevel1,
  } = useCollectiveMembers({
    ...baseParams,
    relationship_status: 'PENDING',
    page: pageLevel1,
    size,
    refreshTrigger: refreshTriggerLevel1,
  });

  const {
    data: dataLevel2,
    isLoading: loadingLevel2,
    error: errorLevel2,
  } = useCollectiveMembers({
    ...baseParams,
    relationship_status: 'ACCEPTED',
    page: pageLevel2,
    size,
    refreshTrigger: refreshTriggerLevel2,
  });

  const {
    data: dataOthers,
    isLoading: loadingOthers,
    error: errorOthers,
  } = useCollectiveMembers({
    ...baseParams,
    page: pageOthers,
    size,
    refreshTrigger: refreshTriggerOthers,
  });

  // Create refresh functions for each section
  const refreshLevel1 = () => setRefreshTriggerLevel1(prev => prev + 1);
  const refreshLevel2 = () => setRefreshTriggerLevel2(prev => prev + 1);
  const refreshOthers = () => setRefreshTriggerOthers(prev => prev + 1);

  // Combined refresh function that refreshes all sections
  const refreshAllData = () => {
    refreshLevel1();
    refreshLevel2();
    refreshOthers();
  };

  return (
    <Box p={0}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <FilterButton
          onClick={() => setFilterOpen(true)}
          locked={filter.applyGlobally}
          ariaLabel="Open filters"
        />
        <Typography variant="h6" fontWeight="bold">
          Comunidade
        </Typography>
      </Stack>
      <CollectiveFilterModal open={filterOpen} onClose={() => setFilterOpen(false)} />
      <Stack direction="column" gap={2}>
        <Accordion defaultExpanded sx={{ borderRadius: 1, background: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack width="100%">
              <Stack direction="row" width="100%" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Solicitações ({dataLevel1?.total ?? 0})
                </Typography>
                {/* <Divider sx={{ flex: 1, mx: 1 }} /> */}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
                (solicitações de conexões recebidas e enviadas)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {loadingLevel1 ? (
              <Spin centerHorizontally centerVertically />
            ) : errorLevel1 ? (
              <Typography color="error">Erro ao carregar solicitações</Typography>
            ) : (dataLevel1?.total ?? 0) === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                <Typography color="text.secondary">Nenhum usuário encontrado.</Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {(dataLevel1?.items ?? []).map(member => (
                    <Grid item xs={12} sm={6} md={4} key={member.uuid}>
                      <MemberCard
                        member={member}
                        loggedUserId={user?.id ?? ''}
                        onConnectionAction={refreshAllData}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box mt={2} display="flex" justifyContent="center">
                  <Pagination
                    totalPages={dataLevel1?.pages ?? 1}
                    currentPage={pageLevel1}
                    onPageChange={setPageLevel1}
                  />
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ borderRadius: 1, background: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack width="100%">
              <Stack direction="row" width="100%" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Conexões ({dataLevel2?.total ?? 0})
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
                (conexões estabelecidas)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {loadingLevel2 ? (
              <Spin centerHorizontally centerVertically />
            ) : errorLevel2 ? (
              <Typography color="error">Erro ao carregar conexões</Typography>
            ) : (dataLevel2?.total ?? 0) === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                <Typography color="text.secondary">Nenhum usuário encontrado.</Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {(dataLevel2?.items ?? []).map(member => (
                    <Grid item xs={12} sm={6} md={4} key={member.uuid}>
                      <MemberCard
                        member={member}
                        loggedUserId={user?.id ?? ''}
                        onConnectionAction={refreshAllData}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box mt={2} display="flex" justifyContent="center">
                  <Pagination
                    totalPages={dataLevel2?.pages ?? 1}
                    currentPage={pageLevel2}
                    onPageChange={setPageLevel2}
                  />
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded sx={{ borderRadius: 1, background: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack width="100%">
              <Stack direction="row" width="100%" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Membros ({dataOthers?.total ?? 0})
                </Typography>
              </Stack>
              {/* <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
                (são conexões das suas conexões)
              </Typography> */}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {loadingOthers ? (
              <Spin centerHorizontally centerVertically />
            ) : errorOthers ? (
              <Typography color="error">Erro ao carregar membros (Todos)</Typography>
            ) : (dataOthers?.total ?? 0) === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                <Typography color="text.secondary">Nenhum usuário encontrado.</Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {(dataOthers?.items ?? []).map(member => (
                    <Grid item xs={12} sm={6} md={4} key={member.uuid}>
                      <MemberCard
                        member={member}
                        loggedUserId={user?.id ?? ''}
                        onConnectionAction={refreshAllData}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box mt={2} display="flex" justifyContent="center">
                  <Pagination
                    totalPages={dataOthers?.pages ?? 1}
                    currentPage={pageOthers}
                    onPageChange={setPageOthers}
                  />
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
  );
};

export default CollectiveProfileMembersPage;
